// Per-segment feature layout for the v2 practical-lesson routes. No Phaser, no config
// imports. Coordinates are SEGMENT-LOCAL world px: 0 is the segment's left edge, 2557 (see
// carCatalog's SEGMENT_WORLD_WIDTH) its right edge, matching the direction of travel (the
// car drives left, so x decreases as it goes — a span `{x0, x1}` is entered at x0, the
// larger value, and exited at x1, same convention as v1's zone tables).
//
// Every segment is traced from the real art (scripts/trace_segments.py) — see
// ~/Documents/stworz-gre/seg-<id>.png; re-run the trace and re-read the numbers if a
// segment's halves are ever regenerated. lessonScript.mjs's buildTasks() is built against
// the SHAPE each segment id carries, so keep the keys when retuning the numbers.

import { SEGMENT_WORLD_WIDTH } from '../ui/carCatalog.mjs';

export const SEGMENT_FEATURES = {
  country: {},
  // sign50: the "50" speed-limit post, traced at composite x~1915 (pole centre averaged
  // over y=290-440 in seg-limit50.png).
  limit50: { sign50: { x: 1915 } },
  // sideRoad: centre of the side-road opening in the kerb, traced at x~340 (kerb-cut spans
  // roughly x224-452 in seg-stop.png; the art has no separately painted stop bar, so
  // stopLine is placed 200px before sideRoad — same gap the placeholder used — giving an
  // ~88px buffer before the kerb-cut's near edge, which is where a real stop line would sit).
  // stopSign: the B-20 is a SPRITE the scene places (its painted twin was erased from the
  // half on 2026-09-02) — it stands right at the stop line, where a real one does.
  stop: { stopSign: { x: 600 }, stopLine: { x: 540 }, sideRoad: { x: 340 } },
  // The art paints the "shark teeth" give-way triangles ON the junction mouth (x230-410 in
  // seg-yield.png; kerb-cut ~x262-422, centre ~340 = sideRoad). The side-road car pulls out through that mouth and is "through" only
  // 140 px past it, so the judged line keeps `stop`'s 200 px gap to sideRoad rather than
  // sitting on the teeth — the bonnet waits ~130 px short of them.
  // yieldSign: a SPRITE (yellow A-7), 3 m before the judged line — the painted one 14 m out
  // was erased.
  yield: { yieldSign: { x: 840 }, yieldLine: { x: 540 }, sideRoad: { x: 340 } },
  // zebra: six painted stripes span x452-771 — x0 is the near edge (entered first), x1 the
  // far edge.
  // crossingSign: a SPRITE (D-6) at the zebra's near edge, where a real one stands — the
  // painted one 11 m out was erased.
  crossing: { crossingSign: { x: 800 }, zebra: { x0: 775, x1: 450 } },
  // The art has the side road (kerb-cut x270-411, centre ~340) but NO painted stop bar
  // and no signal post (the light is a 3-state sprite), so stopLine sits ~90 px before the
  // kerb-cut's near edge like `stop`'s, and lightPole 60 px before the line (spec).
  lights: { stopLine: { x: 500 }, lightPole: { x: 560 } },
  // noOvertakeSign: the B-25 post at x~1901; the solid centre line runs the whole segment.
  // parkedCar is a sprite (no art to trace) — kept where the overtake window was tuned.
  solid: { noOvertakeSign: { x: 1901 }, parkedCar: { x: 1300 } },
  // sign30: the A-17 + B-33 "30" post, pole centre at x~1915.
  school: { sign30: { x: 1915 } },
  // The B-2 post stands in the mouth of the side street itself (kerb-cut ~x500-740 in
  // seg-noentry.png), so the sign and the street are practically the same x. Informational
  // only — nothing is judged here (see lessonScript's `noentry` task).
  noentry: { noEntrySign: { x: 640 }, sideStreet: { x: 620 } },
  // parkingSign: the D-18 "P" post at x~1627. One continuous lay-by in one style (2026-09-03,
  // compose_segments.retile_parking_bay): a single slanted-line tile of the sign half's bay is
  // laid across the whole bottom band at a 480 px pitch, so the lines stand at x~2180, 1700,
  // 1220, 740 and 260 (read at the band's mid-height) — four 480 px spaces, with a stub at
  // each end that is narrower than the car and so not a bay. The school's Yaris is ~360 px
  // wide and routeRules' `park` wants the WHOLE car between one bay's lines (±20 px): 160 px
  // of room per space.
  parking: {
    parkingSign: { x: 1627 },
    bays: [
      { x0: 2180, x1: 1700 },
      { x0: 1700, x1: 1220 },
      { x0: 1220, x1: 740 },
      { x0: 740, x1: 260 },
    ],
  },
};

/**
 * Turn a segment-local x into the route's absolute world x. `segmentIndex` is the segment's
 * position in the driving order (0 = first driven, i.e. the rightmost/highest-x segment);
 * `n` is the route's total segment count, so `n * SEGMENT_WORLD_WIDTH` is the route's
 * `worldWidth` (see lessonScript.layoutSegments — the two agree by construction).
 */
export function featureWorldX(segmentIndex, n, localX) {
  const W = n * SEGMENT_WORLD_WIDTH;
  return W - (segmentIndex + 1) * SEGMENT_WORLD_WIDTH + localX;
}
